import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { IonIcon, IonLabel } from '@ionic/angular/standalone';
import { NgClass, NgIf, NgFor, CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { QuillViewComponent } from 'ngx-quill';
import { Platform } from '@ionic/angular/standalone';
import { BtnComponent } from '../btn/btn.component';
import { addIcons } from 'ionicons';
import gsap from 'gsap';
import {
  mailOpen,
  swapHorizontalOutline,
  chevronBackOutline,
  chevronForwardOutline,
  chevronUpOutline,
  chevronDownOutline,
  repeatOutline,
  albumsOutline,
  play,
  pause,
  volumeHigh,
  volumeMute,
  expandOutline,
  contractOutline,
  closeOutline,
} from 'ionicons/icons';

/** Generic item interface for sticker/card data - works with different data shapes */
export interface V2DetailOverlayItem {
  item_id?: string | number;
  item_name?: string;
  icon_path?: string;
  Tenho?: boolean;
  orientation?: 'L' | 'P';
  video?: string;
  description_1?: any;
  description_2?: any;
  section_name?: string;
  icon_color?: string;
  [key: string]: any;
}

export interface V2DetailOverlaySection {
  section_name: string;
  item: V2DetailOverlayItem[];
}

export interface V2DetailOverlayCollectionDetails {
  collection_tag?: string;
  collection_type?: string;
  icon_location_prefix?: string;
  [key: string]: any;
}

export type V2DetailViewMode = 'slideView' | 'videoView';

/** Emitted when the inline `<video>` reports loading / ready / error (replaces DOM id hacks). */
export type V2DetailVideoLoadState = 'loading' | 'ready' | 'error';

@Component({
  selector: 'app-v2-detail-overlay',
  standalone: true,
  imports: [
    CommonModule,
    NgClass,
    NgIf,
    NgFor,
    IonIcon,
    IonLabel,
    TranslatePipe,
    QuillViewComponent,
    BtnComponent,
  ],
  templateUrl: './v2-detail-overlay.component.html',
  styleUrls: ['./v2-detail-overlay.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2DetailOverlayComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('v2ReelsRoot', { read: ElementRef }) v2ReelsRootEl!: ElementRef<HTMLDivElement>;
  @ViewChild('v2VideoPlayer') v2VideoPlayer?: ElementRef<HTMLVideoElement>;
  @ViewChild('v2VideoStage') v2VideoStage?: ElementRef<HTMLElement>;
  @ViewChild('overlayRoot', { read: ElementRef }) overlayRootEl?: ElementRef<HTMLDivElement>;
  @ViewChild('detailContentStage', { read: ElementRef }) detailContentStageEl?: ElementRef<HTMLElement>;
  @ViewChild('detailSlideWrap', { read: ElementRef }) detailSlideWrapEl?: ElementRef<HTMLElement>;

  /** Whether overlay is visible */
  @Input() isOpen = false;

  /** Items in current section (flip card reels) */
  @Input() items: V2DetailOverlayItem[] = [];

  /** All sections (for section badge index) */
  @Input() sections: V2DetailOverlaySection[] = [];

  /** Index of current section within sections */
  @Input() currentSectionIndex = 0;

  /** Index of selected item within current section */
  @Input() selectedIndex = 0;

  /** Collection details (collection_tag, icon_location_prefix, etc.) */
  @Input() collectionDetails: V2DetailOverlayCollectionDetails | null = null;

  /** Prefix for sticker image URLs (e.g. from collectionDetails.icon_location_prefix) */
  @Input() iconLocationPrefix = '';

  /** View mode: slideView (flip cards) or videoView */
  @Input() viewMode: V2DetailViewMode = 'slideView';

  /** Show video view (when play is pressed) */
  @Input() showVideo = true;

  /** Show footer with action buttons */
  @Input() showFooter = true;

  /**
   * When true (typical phone), reel column (prev / flip / next) is hidden — use gestures.
   * Album shortcut stays available as the top-left floating control when enabled.
   */
  @Input() isMobile = false;

  /** Trade UI: ORANGE = pending request (CHECK_REQUEST + SWAP), BLUE/other = SWAP (trade options). */
  @Input() iconColor = '';

  /** Video source URL when in video view */
  @Input() videoSrc = '';

  /** Video aspect ratio */
  @Input() aspectRatio: 'LANDSCAPE' | 'PORTRAIT' = 'PORTRAIT';

  /** Whether video has loaded */
  @Input() hasVideoLoaded = false;

  /** Show album shortcut in reel nav (gesture “V” collections; parent wires `onOpenAlbum`). */
  @Input() showOpenAlbumButton = false;

  /** Emitted when overlay close is requested */
  @Output() closed = new EventEmitter<void>();

  /** Emitted when header back is pressed in video view — parent should return to slide view without closing the overlay. */
  @Output() videoBack = new EventEmitter<void>();

  /** Emitted when selected item changes (index within section) */
  @Output() itemChange = new EventEmitter<{ index: number; item: V2DetailOverlayItem }>();

  /** Emitted when user requests to play video */
  @Output() playVideo = new EventEmitter<{ index: number; item: V2DetailOverlayItem }>();

  /** Emitted when user swipes to next/prev section (parent should load new section) */
  @Output() sectionChangeRequest = new EventEmitter<'next' | 'prev'>();

  /** Emitted when video ends */
  @Output() videoEnded = new EventEmitter<void>();

  /** Emitted when the `<video>` element reports load lifecycle (parent updates `hasVideoLoaded` via service). */
  @Output() videoLoadState = new EventEmitter<{ state: V2DetailVideoLoadState }>();

  /** Emitted when footer action is clicked (exchangeOrange, exchangeBlue) */
  @Output() footerAction = new EventEmitter<{ action: string; item: V2DetailOverlayItem | null }>();

  /** Emitted when user opens album view for the current sticker’s page */
  @Output() openAlbum = new EventEmitter<{ itemId: string }>();

  /**
   * While true, footer plays the collapse animation from two buttons → Swap-only
   * (ORANGE → non-ORANGE). Cleared after the animation duration so first paint
   * without ORANGE never animates from a fake “two column” state.
   */
  footerMorphReverseFromOrange = false;

  private footerMorphReverseFromOrangeTimer: ReturnType<typeof setTimeout> | null = null;

  /** Matches footer morph keyframe duration in SCSS (0.72s) */
  private static readonly FOOTER_MORPH_MS = 720;

  /** Matches `v2-detail-overlay-enter` / `v2-detail-overlay-exit` duration in SCSS (0.4s) */
  private static readonly OVERLAY_CLOSE_ANIM_MS = 400;

  /**
   * True while the overlay root is in the DOM (stays true during exit animation after `isOpen` is false).
   */
  domOpen = false;

  /** True while playing the CSS exit animation (inverse of enter). */
  isClosing = false;

  /**
   * Hides slide chrome until `runSlideWrapPackIntro` runs (same pattern as v3 pack frame intro-pending).
   */
  detailSlideWrapIntroPending = false;

  private slideWrapPackTween?: gsap.core.Tween;

  private closeAnimationFinishTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Header/footer use ~0.6s transitions when leaving video; wait for layout to settle
   * before gestures + sticker height remeasure (avoids wrong reel height).
   */
  private static readonly SLIDE_VIEW_AFTER_VIDEO_CHROME_MS = 650;

  private slideViewAfterVideoLayoutTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly VIDEO_LOADER_TEXT_INTERVAL_MS = 4000;

  /** localStorage: user dismissed the “tap to unmute” hint globally. */
  private static readonly VIDEO_UNMUTE_HINT_LS_KEY = 'v2DetailVideoUnmuteHintDismissed';

  /** Inline video error UI (empty src or `error` event). */
  videoElementError = false;

  /** True after `canplay` on the real `<video>` (immediate UI; parent `hasVideoLoaded` may lag one tick). */
  inlineVideoReady = false;

  /** Alternates loader copy while waiting (replaces parent `setInterval`). */
  videoLoaderAltText = false;

  private videoLoaderTextInterval: ReturnType<typeof setInterval> | null = null;

  /** Avoid duplicate `ready` emissions per `videoSrc`. */
  private videoReadyEmittedForSrc = '';

  /** Custom video UI (no native `controls`). */
  videoPlaying = false;
  videoMuted = true;
  videoCurrentTime = 0;
  videoDuration = 0;
  /** Bottom chrome + center play; fades while playing. */
  videoChromeVisible = true;
  videoIsFullscreen = false;

  private videoChromeHideTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly VIDEO_CHROME_AUTOHIDE_MS = 3200;
  private videoTimeupdateThrottleAt = 0;

  /**
   * End-of-flick horizontal speed (px/s) under this: snap by angle only (distance threshold).
   * Above: add a virtual “coast” in degrees so quick short swipes still flip the card.
   */
  private static readonly FLIP_FLICK_MIN_VELOCITY_PX_S = 220;
  /**
   * Pretend the card coasts for this duration at release velocity; converted to extra ° via
   * `getFlipDegreesPerPixel` so a fast flick can cross the ~90° face boundary.
   */
  private static readonly FLIP_FLICK_HORIZON_SEC = 0.16;
  /** Cap the velocity boost (deg) to avoid a wild throw from always jumping 360°+ */
  private static readonly FLIP_FLICK_MAX_BOOST_DEG = 115;

  /** 0–100: max buffered position vs duration (see `progress` on &lt;video&gt;). */
  videoBufferedPercent = 0;
  private videoProgressRaf = 0;

  /** After first `play`, show unmute hint while still muted (unless dismissed). */
  videoHasStartedPlayback = false;

  /** User hid the unmute hint (session) or previously via localStorage. */
  videoUnmuteHintDismissed = false;

  /** Chrome auto-hide only after enough data (`canplaythrough` or `playing`). */
  private videoChromeAutohideAllowed = false;

  private readonly videoFullscreenListener = (): void => {
    const doc = document as Document & { webkitFullscreenElement?: Element | null };
    this.videoIsFullscreen = !!(document.fullscreenElement ?? doc.webkitFullscreenElement);
    this.cdr.markForCheck();
  };

  // Internal state
  sectionItems: V2DetailOverlayItem[] = [];
  currIndexInSection = 0;
  reelImagesLoaded: { [key: string]: boolean } = {};
  reelHeight = 0;
  trackOffset = 0;
  dragOffset = 0;
  trackTransition = true;
  readonly reelGap = 16;
  /** Active sticker flip angle (deg); 0 = front, 180 = back. */
  flipRotationY = 0;
  /** While true, flip inner uses no CSS transition (finger-driven rotation). */
  flipDragActive = false;
  private gestureCleanup: (() => void) | null = null;

  /** Mobile slide view: bottom hint (v3 tap-hint styling); hidden after first swipe / action. */
  showSwipeHintMobile = true;

  /**
   * Rendered size (px) of each front sticker image under object-fit: contain — the visible
   * bitmap is often smaller than the frame (baked-in borders). The back is sized to match.
   */
  stickerPaintedSize: { [itemId: string]: { w: number; h: number } } = {};
  private resizeObserver: ResizeObserver | null = null;
  private remeasurePaintedRaf = 0;

  /** Footer pill entrance animation (classes `opened` / `loaded` on `.v2-detail-footer-inner`). */
  footerChromeReveal = false;
  private footerChromeRevealRaf1 = 0;
  private footerChromeRevealRaf2 = 0;

  get reelStep(): number {
    return this.reelHeight + this.reelGap;
  }

  get trackTransform(): string {
    return `translateY(${this.trackOffset + this.dragOffset}px)`;
  }

  get sectionTitle(): string {
    const section = this.sections?.[this.currentSectionIndex];
    return section?.section_name ?? '';
  }

  get currentItem(): V2DetailOverlayItem | null {
    return this.sectionItems?.[this.currIndexInSection] ?? null;
  }

  /** Accessible name for the inline video (section + sticker title). */
  get videoAriaLabel(): string {
    const section = this.sectionTitle;
    const name = this.currentItem?.item_name ?? '';
    if (section && name) {
      return `${section} — ${name}`;
    }
    if (section) {
      return section;
    }
    if (name) {
      return name;
    }
    return 'Video';
  }

  /**
   * Unmute hint row is shown — keep chrome visible even when auto-hide would fade the whole bar
   * (`v2-detail-video-chrome--hidden`), otherwise the hint is invisible while the video plays muted.
   */
  get videoUnmuteHintShown(): boolean {
    return (
      this.videoMuted &&
      this.videoHasStartedPlayback &&
      !this.videoUnmuteHintDismissed &&
      this.inlineVideoReady
    );
  }

  /**
   * API often sends Tenho as boolean true **or** numeric 1 (see user-collection-items).
   * Strict `=== true` in templates hid the landscape back for owned cards.
   */
  hasTenho(item: V2DetailOverlayItem | null | undefined): boolean {
    const t = item?.Tenho as unknown;
    return t === true || t === 1;
  }

  isLandscapeOrientation(item: V2DetailOverlayItem | null | undefined): boolean {
    const o = item?.orientation;
    if (o === undefined || o === null) return false;
    return String(o).toUpperCase() === 'L';
  }

  /** Vertical back template: portrait / default orientation, or sticker not owned */
  showBackPortraitOrMissing(item: V2DetailOverlayItem | null | undefined): boolean {
    return !this.isLandscapeOrientation(item) || !this.hasTenho(item);
  }

  /** Horizontal back template: owned landscape stickers only */
  showBackLandscapeOwned(item: V2DetailOverlayItem | null | undefined): boolean {
    return this.isLandscapeOrientation(item) && this.hasTenho(item);
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private platform: Platform
  ) {
    /* Icons used in footer/header; not all are registered globally in app.component */
    addIcons({
      chevronBackOutline,
      chevronForwardOutline,
      chevronUpOutline,
      chevronDownOutline,
      repeatOutline,
      albumsOutline,
      mailOpen,
      swapHorizontalOutline,
      play,
      pause,
      volumeHigh,
      volumeMute,
      expandOutline,
      contractOutline,
      closeOutline,
    });
  }

  ngOnInit(): void {
    if (typeof localStorage !== 'undefined') {
      this.videoUnmuteHintDismissed =
        localStorage.getItem(V2DetailOverlayComponent.VIDEO_UNMUTE_HINT_LS_KEY) === '1';
    }
  }

  formatVideoTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) {
      return '0:00';
    }
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['iconColor'] && !changes['iconColor'].firstChange) {
      const prev = changes['iconColor'].previousValue;
      const cur = changes['iconColor'].currentValue;
      if (prev === 'ORANGE' && cur !== 'ORANGE') {
        if (this.footerMorphReverseFromOrangeTimer) {
          clearTimeout(this.footerMorphReverseFromOrangeTimer);
          this.footerMorphReverseFromOrangeTimer = null;
        }
        this.footerMorphReverseFromOrange = true;
        this.cdr.markForCheck();
        this.footerMorphReverseFromOrangeTimer = setTimeout(() => {
          this.footerMorphReverseFromOrange = false;
          this.footerMorphReverseFromOrangeTimer = null;
          this.cdr.markForCheck();
        }, V2DetailOverlayComponent.FOOTER_MORPH_MS);
      } else if (prev !== 'ORANGE' && cur === 'ORANGE') {
        if (this.footerMorphReverseFromOrangeTimer) {
          clearTimeout(this.footerMorphReverseFromOrangeTimer);
          this.footerMorphReverseFromOrangeTimer = null;
        }
        this.footerMorphReverseFromOrange = false;
        this.cdr.markForCheck();
      }
    }

    if (changes['viewMode'] || changes['videoSrc']) {
      this.syncInlineVideoLoadLifecycle();
    }

    if (
      changes['viewMode'] &&
      !changes['viewMode'].firstChange &&
      changes['viewMode'].previousValue === 'slideView' &&
      this.viewMode === 'videoView'
    ) {
      this.cancelSlideWrapPackIntro();
    }

    /* Returning from videoView → slideView: wait for header/footer chrome to finish animating in, then measure. */
    if (
      changes['viewMode'] &&
      !changes['viewMode'].firstChange &&
      this.isOpen &&
      this.viewMode === 'slideView' &&
      changes['viewMode'].previousValue === 'videoView'
    ) {
      if (this.slideViewAfterVideoLayoutTimer) {
        clearTimeout(this.slideViewAfterVideoLayoutTimer);
        this.slideViewAfterVideoLayoutTimer = null;
      }
      const delayMs = this.getSlideViewAfterVideoChromeSettleMs();
      this.slideViewAfterVideoLayoutTimer = setTimeout(() => {
        this.slideViewAfterVideoLayoutTimer = null;
        this.initGestures();
        this.setupResizeObserver();
        this.remeasureAllStickerPaintSizes();
        this.resetSwipeHintMobile();
      }, delayMs);
    }

    if (changes['isOpen']) {
      if (this.isOpen) {
        this.clearCloseAnimationFinishTimer();
        const wasClosing = this.isClosing;
        this.isClosing = false;
        this.domOpen = true;
        if (wasClosing) {
          setTimeout(() => this.resetOverlayEnterAnimationStyles(), 0);
        }
      } else {
        this.cancelFooterChromeRevealAnimation();
        this.cancelSlideWrapPackIntro();
        this.footerChromeReveal = false;
        if (this.domOpen) {
          this.isClosing = true;
          this.onOverlayCloseAnimationStart();
          this.scheduleCloseAnimationFinish();
        }
      }
    }

    if (
      changes['items'] ||
      changes['selectedIndex'] ||
      changes['sections'] ||
      changes['currentSectionIndex'] ||
      changes['isOpen']
    ) {
      this.buildSectionTrack(changes);
    }

    if (changes['isOpen'] && this.isOpen) {
      this.applyOverlayOpenEffects();
    }

    if (
      changes['isMobile'] &&
      !changes['isMobile'].firstChange &&
      this.isOpen &&
      this.viewMode === 'slideView'
    ) {
      if (this.isMobile) {
        this.resetSwipeHintMobile();
      } else {
        this.dismissSwipeHintMobile();
      }
    }
  }

  ngAfterViewInit(): void {
    this.buildSectionTrack(undefined);
    this.setupResizeObserver();
    if (typeof document !== 'undefined') {
      document.addEventListener('fullscreenchange', this.videoFullscreenListener);
      document.addEventListener('webkitfullscreenchange', this.videoFullscreenListener as EventListener);
    }
  }

  ngOnDestroy(): void {
    if (this.slideViewAfterVideoLayoutTimer) {
      clearTimeout(this.slideViewAfterVideoLayoutTimer);
      this.slideViewAfterVideoLayoutTimer = null;
    }
    this.clearVideoChromeHideTimer();
    if (this.videoProgressRaf) {
      cancelAnimationFrame(this.videoProgressRaf);
      this.videoProgressRaf = 0;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('fullscreenchange', this.videoFullscreenListener);
      document.removeEventListener('webkitfullscreenchange', this.videoFullscreenListener as EventListener);
    }
    this.stopVideoLoaderTextInterval();
    if (this.footerMorphReverseFromOrangeTimer) {
      clearTimeout(this.footerMorphReverseFromOrangeTimer);
      this.footerMorphReverseFromOrangeTimer = null;
    }
    this.clearCloseAnimationFinishTimer();
    this.cancelFooterChromeRevealAnimation();
    this.cancelSlideWrapPackIntro();
    this.gestureCleanup?.();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.remeasurePaintedRaf) {
      cancelAnimationFrame(this.remeasurePaintedRaf);
      this.remeasurePaintedRaf = 0;
    }
    this.setBodyScrollLock(false);
  }

  /** After `buildSectionTrack` when `isOpen` becomes true (order matches previous ngOnChanges). */
  private applyOverlayOpenEffects(): void {
    this.scheduleFooterChromeRevealAnimation();
    this.setBodyScrollLock(true);
    if (this.viewMode === 'slideView') {
      if (!this.prefersReducedMotion()) {
        this.detailSlideWrapIntroPending = true;
        this.cdr.markForCheck();
      }
      queueMicrotask(() => {
        requestAnimationFrame(() => this.runSlideWrapPackIntro());
      });
      setTimeout(() => this.initGestures(), 150);
      setTimeout(() => {
        this.setupResizeObserver();
        this.remeasureAllStickerPaintSizes();
      }, 200);
      this.resetSwipeHintMobile();
    }
  }

  private resetSwipeHintMobile(): void {
    if (!this.isMobile || this.viewMode !== 'slideView' || !this.isOpen) {
      return;
    }
    this.showSwipeHintMobile = true;
    this.cdr.markForCheck();
  }

  private dismissSwipeHintMobile(): void {
    if (!this.showSwipeHintMobile) {
      return;
    }
    this.showSwipeHintMobile = false;
    this.cdr.markForCheck();
  }

  private getOverlayCloseAnimDurationMs(): number {
    return this.prefersReducedMotion() ? 0 : V2DetailOverlayComponent.OVERLAY_CLOSE_ANIM_MS;
  }

  private clearCloseAnimationFinishTimer(): void {
    if (this.closeAnimationFinishTimer) {
      clearTimeout(this.closeAnimationFinishTimer);
      this.closeAnimationFinishTimer = null;
    }
  }

  private scheduleCloseAnimationFinish(): void {
    this.clearCloseAnimationFinishTimer();
    const ms = this.getOverlayCloseAnimDurationMs();
    this.closeAnimationFinishTimer = setTimeout(() => {
      this.closeAnimationFinishTimer = null;
      this.isClosing = false;
      this.domOpen = false;
      this.onOverlayCloseAnimationComplete();
      this.cdr.markForCheck();
    }, ms);
  }

  /** Stop gestures / video helpers as soon as close starts; body scroll stays locked until exit ends. */
  private onOverlayCloseAnimationStart(): void {
    this.stopVideoLoaderTextInterval();
    this.gestureCleanup?.();
    this.showSwipeHintMobile = true;
  }

  private onOverlayCloseAnimationComplete(): void {
    this.setBodyScrollLock(false);
    this.flipRotationY = 0;
    this.flipDragActive = false;
    this.cdr.markForCheck();
  }

  /**
   * After cancelling mid-exit, restore full opacity/scale so the shell overlay isn’t stuck at the exit keyframe.
   */
  private resetOverlayEnterAnimationStyles(): void {
    const el = this.overlayRootEl?.nativeElement;
    if (!el) return;
    el.style.animation = 'none';
    el.style.opacity = '1';
    el.style.transform = 'scale(1)';
    void el.offsetWidth;
    el.style.removeProperty('animation');
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
    this.resetDetailContentEnterAnimationStyles();
    this.resetSlideWrapPackIntroStyles();
  }

  /** Same motion as v3 `runPackIntro()` on the pack frame (back.out, 0.85s). */
  private runSlideWrapPackIntro(): void {
    const wrap = this.detailSlideWrapEl?.nativeElement;
    this.slideWrapPackTween?.kill();
    this.slideWrapPackTween = undefined;

    if (this.prefersReducedMotion()) {
      this.detailSlideWrapIntroPending = false;
      if (wrap) {
        gsap.killTweensOf(wrap);
        gsap.set(wrap, { clearProps: 'opacity,transform' });
      }
      this.cdr.markForCheck();
      return;
    }

    if (!wrap || this.viewMode !== 'slideView' || !this.isOpen) {
      this.detailSlideWrapIntroPending = false;
      this.cdr.markForCheck();
      return;
    }

    const yFrom =
      typeof window !== 'undefined' ? Math.min(window.innerHeight * 0.48, 520) : 400;

    gsap.set(wrap, {
      transformOrigin: 'center center',
      opacity: 0,
      scale: 0.44,
      y: yFrom,
      x: 0,
    });
    this.detailSlideWrapIntroPending = false;
    this.cdr.markForCheck();

    this.slideWrapPackTween = gsap.fromTo(
      wrap,
      { opacity: 0, scale: 0.44, y: yFrom, x: 0 },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        x: 0,
        duration: 0.85,
        ease: 'back.out(1.42)',
        onComplete: () => {
          this.slideWrapPackTween = undefined;
        },
      }
    );
  }

  private cancelSlideWrapPackIntro(): void {
    this.slideWrapPackTween?.kill();
    this.slideWrapPackTween = undefined;
    const wrap = this.detailSlideWrapEl?.nativeElement;
    if (wrap) {
      gsap.killTweensOf(wrap);
      gsap.set(wrap, { clearProps: 'opacity,transform' });
    }
    this.detailSlideWrapIntroPending = false;
  }

  /** Clears GSAP inline transforms when reopening mid-close (pairs with `resetDetailContentEnterAnimationStyles`). */
  private resetSlideWrapPackIntroStyles(): void {
    this.cancelSlideWrapPackIntro();
  }

  prefersReducedMotion(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Restarts the main content intro (same pattern as v2-album-overlay `.album-card-block`). */
  private resetDetailContentEnterAnimationStyles(): void {
    const el = this.detailContentStageEl?.nativeElement;
    if (!el) return;
    el.style.animation = 'none';
    el.style.opacity = '1';
    el.style.transform = 'none';
    void el.offsetWidth;
    el.style.removeProperty('animation');
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
  }

  private scheduleFooterChromeRevealAnimation(): void {
    this.cancelFooterChromeRevealAnimation();
    this.footerChromeReveal = false;
    this.cdr.markForCheck();
    this.footerChromeRevealRaf1 = requestAnimationFrame(() => {
      this.footerChromeRevealRaf1 = 0;
      this.footerChromeRevealRaf2 = requestAnimationFrame(() => {
        this.footerChromeRevealRaf2 = 0;
        this.footerChromeReveal = true;
        this.cdr.markForCheck();
      });
    });
  }

  private cancelFooterChromeRevealAnimation(): void {
    if (this.footerChromeRevealRaf1) {
      cancelAnimationFrame(this.footerChromeRevealRaf1);
      this.footerChromeRevealRaf1 = 0;
    }
    if (this.footerChromeRevealRaf2) {
      cancelAnimationFrame(this.footerChromeRevealRaf2);
      this.footerChromeRevealRaf2 = 0;
    }
  }

  private setBodyScrollLock(locked: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('v2-detail-overlay-open', locked);
  }

  /**
   * Rebuilds reel/track state from @Input()s.
   * When the parent echoes the same selection after a swipe/chevron (emitItemChange → service.update),
   * we must NOT reset trackTransition or reelImagesLoaded — that was cancelling the CSS transform
   * animation and making the track snap instantly.
   */
  private buildSectionTrack(changes?: SimpleChanges): void {
    this.sectionItems = this.items ?? [];
    const idx = Math.max(0, Math.min(this.selectedIndex, this.sectionItems.length - 1));
    this.reelHeight = this.reelHeight || this.platform.height();
    const newTrackOffset = -idx * this.reelStep;

    const positionAlreadyCorrect =
      this.currIndexInSection === idx &&
      Math.abs(this.trackOffset - newTrackOffset) < 0.5 &&
      this.dragOffset === 0;

    /** Opening / section change / new items list — must run full layout reset */
    const structuralChange =
      !changes ||
      !!changes['sections'] ||
      !!changes['currentSectionIndex'] ||
      (!!changes['isOpen'] && this.isOpen);

    if (changes && positionAlreadyCorrect && !structuralChange) {
      this.setBodyScrollLock(this.isOpen || this.isClosing);
      this.cdr.markForCheck();
      return;
    }

    if (!changes || structuralChange) {
      this.stickerPaintedSize = {};
    }

    this.currIndexInSection = idx;
    this.trackOffset = newTrackOffset;
    this.flipRotationY = 0;
    this.trackTransition = false;
    this.reelImagesLoaded = {};
    this.sectionItems.forEach((it) => {
      const id = it?.item_id != null ? String(it.item_id) : undefined;
      if (id) this.reelImagesLoaded[id] = true;
    });
    this.scheduleReelImagesFallback();
    this.setBodyScrollLock(this.isOpen || this.isClosing);
    this.cdr.markForCheck();
    setTimeout(() => this.remeasureAllStickerPaintSizes(), 0);
  }

  /** object-fit: contain — size of the painted bitmap inside cw×ch */
  private computeObjectFitContainSize(
    cw: number,
    ch: number,
    nw: number,
    nh: number
  ): { w: number; h: number } {
    if (!cw || !ch) return { w: 0, h: 0 };
    if (!nw || !nh) return { w: cw, h: ch };
    const scale = Math.min(cw / nw, ch / nh);
    return {
      w: Math.round(nw * scale * 1000) / 1000,
      h: Math.round(nh * scale * 1000) / 1000,
    };
  }

  private measureStickerPaintedSize(img: HTMLImageElement, item: V2DetailOverlayItem): void {
    const id = item?.item_id != null ? String(item.item_id) : '';
    if (!id) return;
    const inner = img.closest('.v2-detail-img-inner') as HTMLElement | null;
    if (!inner || inner.clientWidth < 1 || inner.clientHeight < 1) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const { w, h } = this.computeObjectFitContainSize(
      inner.clientWidth,
      inner.clientHeight,
      nw,
      nh
    );
    if (w > 0 && h > 0) {
      this.stickerPaintedSize[id] = { w, h };
    }
  }

  onFrontStickerImageLoad(event: Event, item: V2DetailOverlayItem, index: number): void {
    this.onReelImageLoad(item, index);
    const img = event.target as HTMLImageElement;
    requestAnimationFrame(() => {
      this.measureStickerPaintedSize(img, item);
      this.cdr.markForCheck();
    });
  }

  getBackSizedStyle(item: V2DetailOverlayItem): Record<string, string> {
    const id = item?.item_id != null ? String(item.item_id) : '';
    const s = id ? this.stickerPaintedSize[id] : undefined;
    if (!s || s.w < 1 || s.h < 1) {
      return {
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        flexShrink: '0',
      };
    }
    return {
      width: `${Math.round(s.w)}px`,
      height: `${Math.round(s.h)}px`,
      maxWidth: '100%',
      maxHeight: '100%',
      flexShrink: '0',
    };
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.scheduleRemeasurePaintedSizes());
    const attach = () => {
      const el = this.v2ReelsRootEl?.nativeElement;
      if (el && this.isOpen && this.viewMode === 'slideView') {
        this.resizeObserver?.observe(el);
      }
    };
    queueMicrotask(attach);
    setTimeout(attach, 200);
    setTimeout(attach, 500);
  }

  private scheduleRemeasurePaintedSizes(): void {
    if (this.remeasurePaintedRaf) cancelAnimationFrame(this.remeasurePaintedRaf);
    this.remeasurePaintedRaf = requestAnimationFrame(() => {
      this.remeasurePaintedRaf = 0;
      this.remeasureAllStickerPaintSizes();
    });
  }

  private remeasureAllStickerPaintSizes(): void {
    if (!this.isOpen || this.viewMode !== 'slideView') return;
    const root = this.v2ReelsRootEl?.nativeElement;
    if (!root) return;
    const imgs = root.querySelectorAll<HTMLImageElement>('img.v2-detail-img[data-item-id]');
    imgs.forEach((img) => {
      if (!img.complete || img.naturalWidth < 1) return;
      const itemId = img.getAttribute('data-item-id');
      if (!itemId) return;
      const item = this.sectionItems.find((i) => String(i?.item_id) === itemId);
      if (item) this.measureStickerPaintedSize(img, item);
    });
    this.cdr.markForCheck();
  }

  private scheduleReelImagesFallback(): void {
    [50, 150, 400, 800].forEach((delay) => {
      setTimeout(() => this.checkReelImagesFallback(), delay);
    });
  }

  private checkReelImagesFallback(): void {
    if (!this.isOpen || this.viewMode !== 'slideView') return;
    const root = this.v2ReelsRootEl?.nativeElement;
    if (!root) return;
    const imgs = root.querySelectorAll<HTMLImageElement>('img[data-item-id]');
    imgs.forEach((img) => {
      const itemId = img.getAttribute('data-item-id');
      if (!itemId || this.reelImagesLoaded[itemId] === false) return;
      if (img.complete && img.naturalWidth > 0) {
        const item = this.sectionItems?.find((i) => String(i?.item_id) === itemId);
        const idx = item ? this.sectionItems?.indexOf(item) ?? -1 : -1;
        if (idx >= 0) this.onReelImageLoad(item, idx);
      }
    });
  }

  getStickerUrl(item: V2DetailOverlayItem): string {
    const prefix = this.iconLocationPrefix || this.collectionDetails?.icon_location_prefix || '';
    return `${prefix}/${item?.icon_path ?? ''}`;
  }

  getReelImageLoading(item: V2DetailOverlayItem): boolean {
    if (!this.isOpen || this.isClosing) {
      return false;
    }
    const id = item?.item_id != null ? String(item.item_id) : '';
    return this.reelImagesLoaded[id] !== false;
  }

  onReelImageLoad(item: V2DetailOverlayItem, _sectionIndex: number): void {
    const id = item?.item_id != null ? String(item.item_id) : '';
    if (id) {
      this.reelImagesLoaded[id] = false;
      this.cdr.markForCheck();
    }
  }

  trackBySectionItem(_index: number, item: V2DetailOverlayItem): string | number {
    return item?.item_id ?? _index;
  }

  hasDescription(descr: any): boolean {
    return descr != null && descr !== undefined && descr !== '';
  }

  /** Chevron: in video view, go back to sticker reels; otherwise close the overlay. */
  onHeaderBack(): void {
    if (this.viewMode === 'videoView') {
      this.videoBack.emit();
    } else {
      this.closed.emit();
    }
  }

  changeSlide(direction: 'B' | 'F'): void {
    this.dismissSwipeHintMobile();
    if (direction === 'B') {
      this.goToPrev();
    } else {
      this.goToNext();
    }
  }

  /** Toggle front/back of the active reel card (button + gesture share flipRotationY). */
  toggleFlipCard(): void {
    if (this.viewMode !== 'slideView' || this.flipDragActive) {
      return;
    }
    this.dismissSwipeHintMobile();
    this.flipRotationY = this.flipRotationY < 90 ? 180 : 0;
    this.cdr.markForCheck();
  }

  onOpenAlbumClick(): void {
    this.dismissSwipeHintMobile();
    const id = this.currentItem?.item_id;
    if (id == null) {
      return;
    }
    this.openAlbum.emit({ itemId: String(id) });
  }

  private goToPrev(): void {
    if (this.currIndexInSection > 0) {
      this.currIndexInSection--;
      this.trackOffset = -this.currIndexInSection * this.reelStep;
      this.trackTransition = true;
      this.flipRotationY = 0;
      this.emitItemChange();
    } else {
      this.flipRotationY = 0;
      this.sectionChangeRequest.emit('prev');
    }
  }

  private goToNext(): void {
    const len = this.sectionItems?.length ?? 0;
    if (this.currIndexInSection < len - 1) {
      this.currIndexInSection++;
      this.trackOffset = -this.currIndexInSection * this.reelStep;
      this.trackTransition = true;
      this.flipRotationY = 0;
      this.emitItemChange();
    } else {
      this.flipRotationY = 0;
      this.sectionChangeRequest.emit('next');
    }
  }

  private emitItemChange(): void {
    const item = this.sectionItems?.[this.currIndexInSection];
    this.itemChange.emit({ index: this.currIndexInSection, item: item ?? ({} as V2DetailOverlayItem) });
    this.cdr.markForCheck();
  }

  setCurrFromReel(sectionIndex: number): void {
    const moved = this.currIndexInSection !== sectionIndex;
    this.currIndexInSection = sectionIndex;
    this.trackOffset = -this.currIndexInSection * this.reelStep;
    if (moved) {
      this.flipRotationY = 0;
    }
    this.emitItemChange();
  }

  onPlayVideo(index: number): void {
    const item = this.sectionItems?.[index];
    if (item) {
      this.setCurrFromReel(index);
      this.playVideo.emit({ index, item });
    }
  }

  onVideoEnd(): void {
    this.videoEnded.emit();
  }

  onV2VideoCanPlay(): void {
    if (this.viewMode !== 'videoView') {
      return;
    }
    const src = this.videoSrc?.trim() ?? '';
    if (!src) {
      return;
    }
    if (this.videoReadyEmittedForSrc === src) {
      return;
    }
    this.videoReadyEmittedForSrc = src;
    this.videoElementError = false;
    this.inlineVideoReady = true;
    this.applyInlineVideoAutoplayMuteDefaults();
    this.emitVideoLoadState('ready');
    this.stopVideoLoaderTextInterval();
    this.cdr.markForCheck();
  }

  onV2VideoError(): void {
    if (this.viewMode !== 'videoView') {
      return;
    }
    this.videoElementError = true;
    this.stopVideoLoaderTextInterval();
    this.emitVideoLoadState('error');
    this.cdr.markForCheck();
  }

  /** User taps retry after a load error. */
  retryInlineVideo(): void {
    const src = this.videoSrc?.trim() ?? '';
    if (!src) {
      return;
    }
    this.videoElementError = false;
    this.inlineVideoReady = false;
    this.videoReadyEmittedForSrc = '';
    this.emitVideoLoadState('loading');
    this.startVideoLoaderTextInterval();
    const el = this.v2VideoPlayer?.nativeElement;
    if (el) {
      el.load();
    }
    this.cdr.markForCheck();
  }

  onVideoStageClick(event: MouseEvent): void {
    const t = event.target as HTMLElement | null;
    if (t?.closest?.('.v2-detail-video-chrome')) {
      return;
    }
    /* First tap while playing + chrome hidden: only reveal controls */
    if (!this.videoChromeVisible && this.videoPlaying) {
      this.videoChromeVisible = true;
      this.scheduleVideoChromeHide();
      this.cdr.markForCheck();
      return;
    }
    this.toggleVideoPlay();
  }

  onVideoLoadedMetadata(): void {
    const el = this.v2VideoPlayer?.nativeElement;
    if (!el) {
      return;
    }
    this.videoDuration = isFinite(el.duration) ? el.duration : 0;
    /* Only until first ready — avoid remuting if metadata fires again after user unmutes. */
    if (!this.inlineVideoReady) {
      this.applyInlineVideoAutoplayMuteDefaults();
    }
    this.videoCurrentTime = el.currentTime;
    this.cdr.markForCheck();
  }

  onVideoTimeUpdate(): void {
    const el = this.v2VideoPlayer?.nativeElement;
    if (!el) {
      return;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.videoTimeupdateThrottleAt < 200) {
      return;
    }
    this.videoTimeupdateThrottleAt = now;
    this.videoCurrentTime = el.currentTime;
    this.cdr.markForCheck();
  }

  onVideoPlayEvent(): void {
    this.videoPlaying = true;
    this.videoHasStartedPlayback = true;
    this.videoChromeVisible = true;
    this.scheduleVideoChromeHide();
    this.cdr.markForCheck();
  }

  /** Enough buffered to play through, or playback actually started — allow chrome auto-hide. */
  onVideoCanPlayThrough(): void {
    if (this.viewMode !== 'videoView') {
      return;
    }
    this.allowVideoChromeAutohide();
  }

  /** Fires when playback begins after enough data (also after seek). */
  onVideoPlayingEvent(): void {
    if (this.viewMode !== 'videoView') {
      return;
    }
    this.allowVideoChromeAutohide();
  }

  onVideoProgress(): void {
    if (this.viewMode !== 'videoView' || this.videoProgressRaf) {
      return;
    }
    this.videoProgressRaf = requestAnimationFrame(() => {
      this.videoProgressRaf = 0;
      this.updateVideoBufferedPercent();
    });
  }

  dismissUnmuteHint(ev: Event): void {
    ev.stopPropagation();
    this.videoUnmuteHintDismissed = true;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(V2DetailOverlayComponent.VIDEO_UNMUTE_HINT_LS_KEY, '1');
    }
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown', ['$event'])
  onVideoKeyboard(event: KeyboardEvent): void {
    if (!this.isOpen || this.viewMode !== 'videoView' || this.videoElementError || !this.inlineVideoReady) {
      return;
    }
    const t = event.target as HTMLElement | null;
    if (t?.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }
    if (t?.closest('button, a, [role="button"]')) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key !== ' ' && key !== 'spacebar' && key !== 'k' && key !== 'm' && key !== 'f') {
      return;
    }
    event.preventDefault();
    if (key === ' ' || key === 'spacebar' || key === 'k') {
      this.toggleVideoPlay();
    } else if (key === 'm') {
      this.toggleVideoMute();
    } else if (key === 'f') {
      this.toggleVideoFullscreen();
    }
    this.cdr.markForCheck();
  }

  onVideoPauseEvent(): void {
    this.videoPlaying = false;
    this.videoChromeVisible = true;
    this.clearVideoChromeHideTimer();
    this.cdr.markForCheck();
  }

  toggleVideoPlay(): void {
    const el = this.v2VideoPlayer?.nativeElement;
    if (!el || this.videoElementError) {
      return;
    }
    this.videoChromeVisible = true;
    if (el.paused) {
      void el.play().catch(() => {});
    } else {
      el.pause();
    }
    this.cdr.markForCheck();
  }

  toggleVideoMute(): void {
    const el = this.v2VideoPlayer?.nativeElement;
    if (!el) {
      return;
    }
    el.muted = !el.muted;
    el.defaultMuted = el.muted;
    this.videoMuted = el.muted;
    this.cdr.markForCheck();
  }

  /**
   * Autoplay policies require muted playback; some browsers briefly report `muted === false`
   * or drop the attribute — force muted + defaultMuted and keep UI in sync.
   */
  private applyInlineVideoAutoplayMuteDefaults(): void {
    const el = this.v2VideoPlayer?.nativeElement;
    if (!el || this.viewMode !== 'videoView') {
      return;
    }
    el.defaultMuted = true;
    el.muted = true;
    this.videoMuted = true;
  }

  toggleVideoFullscreen(): void {
    const stage = this.v2VideoStage?.nativeElement;
    if (!stage || typeof document === 'undefined') {
      return;
    }
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const fsEl = document.fullscreenElement ?? doc.webkitFullscreenElement;
    if (fsEl) {
      void (document.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
    } else {
      const req = stage.requestFullscreen?.bind(stage) ?? (stage as HTMLElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen?.bind(stage);
      if (req) {
        void Promise.resolve(req()).catch(() => {});
      }
    }
  }

  private syncInlineVideoLoadLifecycle(): void {
    if (this.viewMode !== 'videoView') {
      this.stopVideoLoaderTextInterval();
      this.videoElementError = false;
      this.inlineVideoReady = false;
      this.pauseInlineVideoCleanup();
      this.resetInlineVideoChromeState();
      return;
    }
    const src = this.videoSrc?.trim() ?? '';
    this.videoReadyEmittedForSrc = '';
    this.inlineVideoReady = false;
    this.resetInlineVideoChromeState();
    if (!src) {
      this.videoElementError = true;
      this.stopVideoLoaderTextInterval();
      this.emitVideoLoadState('error');
      this.cdr.markForCheck();
      return;
    }
    this.videoElementError = false;
    this.emitVideoLoadState('loading');
    this.startVideoLoaderTextInterval();
    this.cdr.markForCheck();
  }

  private resetInlineVideoChromeState(): void {
    this.videoCurrentTime = 0;
    this.videoDuration = 0;
    this.videoPlaying = false;
    this.videoMuted = true;
    this.videoChromeVisible = true;
    this.videoTimeupdateThrottleAt = 0;
    this.videoBufferedPercent = 0;
    this.videoHasStartedPlayback = false;
    this.videoChromeAutohideAllowed = false;
    this.clearVideoChromeHideTimer();
  }

  private allowVideoChromeAutohide(): void {
    if (this.videoChromeAutohideAllowed) {
      return;
    }
    this.videoChromeAutohideAllowed = true;
    if (this.videoPlaying) {
      this.scheduleVideoChromeHide();
    }
    this.cdr.markForCheck();
  }

  private updateVideoBufferedPercent(): void {
    const el = this.v2VideoPlayer?.nativeElement;
    if (!el) {
      return;
    }
    const d = el.duration;
    if (!isFinite(d) || d <= 0) {
      if (this.videoBufferedPercent !== 0) {
        this.videoBufferedPercent = 0;
        this.cdr.markForCheck();
      }
      return;
    }
    const b = el.buffered;
    let end = 0;
    for (let i = 0; i < b.length; i++) {
      end = Math.max(end, b.end(i));
    }
    const pct = Math.min(100, Math.max(0, (end / d) * 100));
    if (Math.abs(pct - this.videoBufferedPercent) < 0.2) {
      return;
    }
    this.videoBufferedPercent = pct;
    this.cdr.markForCheck();
  }

  private pauseInlineVideoCleanup(): void {
    const el = this.v2VideoPlayer?.nativeElement;
    if (el) {
      el.pause();
    }
  }

  private clearVideoChromeHideTimer(): void {
    if (this.videoChromeHideTimer) {
      clearTimeout(this.videoChromeHideTimer);
      this.videoChromeHideTimer = null;
    }
  }

  private scheduleVideoChromeHide(): void {
    this.clearVideoChromeHideTimer();
    if (!this.videoChromeAutohideAllowed) {
      return;
    }
    if (!this.videoPlaying || !this.inlineVideoReady) {
      return;
    }
    this.videoChromeHideTimer = setTimeout(() => {
      this.videoChromeHideTimer = null;
      this.videoChromeVisible = false;
      this.cdr.markForCheck();
    }, V2DetailOverlayComponent.VIDEO_CHROME_AUTOHIDE_MS);
  }

  private emitVideoLoadState(state: V2DetailVideoLoadState): void {
    this.videoLoadState.emit({ state });
  }

  private startVideoLoaderTextInterval(): void {
    this.stopVideoLoaderTextInterval();
    if (this.viewMode !== 'videoView') {
      return;
    }
    this.videoLoaderAltText = false;
    this.videoLoaderTextInterval = setInterval(() => {
      this.videoLoaderAltText = !this.videoLoaderAltText;
      this.cdr.markForCheck();
    }, V2DetailOverlayComponent.VIDEO_LOADER_TEXT_INTERVAL_MS);
  }

  private stopVideoLoaderTextInterval(): void {
    if (this.videoLoaderTextInterval) {
      clearInterval(this.videoLoaderTextInterval);
      this.videoLoaderTextInterval = null;
    }
  }

  onFooterAction(action: string): void {
    this.footerAction.emit({ action, item: this.currentItem });
  }

  /** Request action only when ORANGE (two-button row is always in DOM). */
  onFooterRequestClick(): void {
    if (this.iconColor !== 'ORANGE') {
      return;
    }
    this.onFooterAction('exchangeOrange');
  }

  /** Delay for header/footer layout after video; 0 when chrome transitions are disabled. */
  private getSlideViewAfterVideoChromeSettleMs(): number {
    if (typeof matchMedia === 'undefined') {
      return V2DetailOverlayComponent.SLIDE_VIEW_AFTER_VIDEO_CHROME_MS;
    }
    return matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : V2DetailOverlayComponent.SLIDE_VIEW_AFTER_VIDEO_CHROME_MS;
  }

  /** ~180° over the active flip card width (px → deg). */
  private getFlipDegreesPerPixel(): number {
    const root = this.v2ReelsRootEl?.nativeElement;
    const cards = root?.querySelectorAll('.v2-detail-flip-card');
    const card = cards?.[this.currIndexInSection] as HTMLElement | undefined;
    const w = card?.getBoundingClientRect().width ?? 200;
    return 180 / Math.max(w, 80);
  }

  /** Snap to nearest 180° (…, -180, 0, 180, 360, …) so multi-rev spins settle on a face. */
  private snapFlipToNearestFace(): void {
    this.flipRotationY = Math.round(this.flipRotationY / 180) * 180;
  }

  /**
   * Same as `snapFlipToNearestFace`, but a fast horizontal release nudges `flipRotationY` first
   * so a short, quick swipe can still commit to the far face (velocity × deg/px × horizon).
   */
  private snapFlipToNearestFaceWithFlick(velocityPxPerSec: number): void {
    if (this.prefersReducedMotion()) {
      this.snapFlipToNearestFace();
      return;
    }
    const v = velocityPxPerSec;
    if (
      !Number.isFinite(v) ||
      Math.abs(v) < V2DetailOverlayComponent.FLIP_FLICK_MIN_VELOCITY_PX_S
    ) {
      this.snapFlipToNearestFace();
      return;
    }
    const degPerPx = this.getFlipDegreesPerPixel();
    const raw =
      v *
      degPerPx *
      V2DetailOverlayComponent.FLIP_FLICK_HORIZON_SEC;
    const cap = V2DetailOverlayComponent.FLIP_FLICK_MAX_BOOST_DEG;
    const boost = Math.sign(raw) * Math.min(Math.abs(raw), cap);
    this.flipRotationY += boost;
    this.snapFlipToNearestFace();
  }

  initGestures(): void {
    const rootEl = this.v2ReelsRootEl?.nativeElement;
    if (!rootEl) return;

    this.gestureCleanup?.();
    this.reelHeight = rootEl.clientHeight;
    this.trackTransition = false;
    this.trackOffset = -this.currIndexInSection * this.reelStep;
    this.dragOffset = 0;
    this.cdr.markForCheck();

    requestAnimationFrame(() => {
      this.trackTransition = true;
      this.cdr.markForCheck();
    });

    const SWIPE_THRESHOLD = 45;
    const DIRECTION_LOCK_THRESHOLD = 12;

    let skipReelFlipGesture = false;
    let directionLock: 'x' | 'y' | null = null;
    let startX = 0;
    let startY = 0;
    let flipRotationAtGestureStart = 0;
    let degreesPerPixel = 180 / 200;
    let lastMoveX = 0;
    let lastMoveT = 0;
    /** Latest horizontal speed (px/s) from last `touchmove` segment — used to snap flip on `touchend`. */
    let lastVelX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('button, a, input, textarea, select, [contenteditable="true"]')) {
        skipReelFlipGesture = true;
        return;
      }
      skipReelFlipGesture = false;
      directionLock = null;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      lastMoveX = startX;
      lastMoveT = performance.now();
      lastVelX = 0;
      flipRotationAtGestureStart = this.flipRotationY;
      degreesPerPixel = this.getFlipDegreesPerPixel();
      this.trackTransition = false;
      this.flipDragActive = true;
      this.cdr.markForCheck();
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (skipReelFlipGesture) {
        return;
      }
      const touch = e.touches[0];
      const now = performance.now();
      const dt = now - lastMoveT;
      if (dt > 0) {
        lastVelX = ((touch.clientX - lastMoveX) / dt) * 1000;
      }
      lastMoveX = touch.clientX;
      lastMoveT = now;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (
        directionLock === null &&
        (absX > DIRECTION_LOCK_THRESHOLD || absY > DIRECTION_LOCK_THRESHOLD)
      ) {
        directionLock = absX > absY ? 'x' : 'y';
        this.dismissSwipeHintMobile();
      }

      if (absX > 5 || absY > 5) {
        e.preventDefault();
      }

      if (directionLock === 'x') {
        /* Signed drag: rotation follows swipe direction; can pass 180, 360, … unclamped until snap. */
        this.flipRotationY = flipRotationAtGestureStart + dx * degreesPerPixel;
        this.dragOffset = 0;
      } else if (directionLock === 'y') {
        this.dragOffset = dy;
        this.flipRotationY = flipRotationAtGestureStart;
      }

      this.cdr.markForCheck();
    };

    const finishReelAndFlipGesture = () => {
      this.trackTransition = true;
      this.flipDragActive = false;

      if (directionLock === 'x') {
        this.dragOffset = 0;
        this.snapFlipToNearestFaceWithFlick(lastVelX);
        this.cdr.markForCheck();
        return;
      }

      if (directionLock === 'y') {
        const dy = this.dragOffset;
        const absY = Math.abs(dy);
        if (absY > SWIPE_THRESHOLD) {
          this.dragOffset = 0;
          this.flipRotationY = 0;
          const len = this.sectionItems?.length ?? 0;
          if (dy < 0) {
            if (this.currIndexInSection < len - 1) {
              this.currIndexInSection++;
              this.trackOffset = -this.currIndexInSection * this.reelStep;
              this.emitItemChange();
            } else {
              this.sectionChangeRequest.emit('next');
            }
          } else {
            if (this.currIndexInSection > 0) {
              this.currIndexInSection--;
              this.trackOffset = -this.currIndexInSection * this.reelStep;
              this.emitItemChange();
            } else {
              this.sectionChangeRequest.emit('prev');
            }
          }
        } else {
          this.dragOffset = 0;
        }
        this.cdr.markForCheck();
        return;
      }

      this.dragOffset = 0;
      this.snapFlipToNearestFace();
      this.cdr.markForCheck();
    };

    const handleTouchEnd = () => {
      if (skipReelFlipGesture) {
        skipReelFlipGesture = false;
        return;
      }
      finishReelAndFlipGesture();
    };

    const handleTouchCancel = () => {
      if (skipReelFlipGesture) {
        skipReelFlipGesture = false;
        return;
      }
      finishReelAndFlipGesture();
    };

    rootEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    rootEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    rootEl.addEventListener('touchend', handleTouchEnd, { passive: true });
    rootEl.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    this.gestureCleanup = () => {
      rootEl.removeEventListener('touchstart', handleTouchStart);
      rootEl.removeEventListener('touchmove', handleTouchMove);
      rootEl.removeEventListener('touchend', handleTouchEnd);
      rootEl.removeEventListener('touchcancel', handleTouchCancel);
      this.gestureCleanup = null;
    };
  }

}

